import { AxiosError } from 'axios';

import type { ApiErrorResponse } from '@/lib/types';

const GENERIC_ERROR = 'Something went wrong on our side. Please try again.';
const NETWORK_ERROR = "Couldn't reach the server. Check your connection and try again.";
const VERIFY_LATER = "We couldn't verify this right now. Please try again in a few minutes.";

const PLATFORM_LABEL: Record<string, string> = {
  telegram: 'Telegram',
  discord: 'Discord',
  x: 'X',
};

function platformLabel(raw: string): string {
  return PLATFORM_LABEL[raw.toLowerCase()] ?? raw;
}

function sentenceCase(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return trimmed;
  const capitalised = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(capitalised) ? capitalised : `${capitalised}.`;
}

/** Copy for bigw-api's stable error codes, when the code alone says enough. */
const CODE_COPY: Record<string, string> = {
  quest_already_completed: "You've already collected this quest.",
  quest_not_found: 'This quest is no longer available.',
  verifier_not_implemented: "This quest can't be collected yet.",
  rate_limited: 'Too many attempts. Wait a minute and try again.',
  unauthorized: 'Your session has expired. Sign in again.',
  validation_error: GENERIC_ERROR,
};

/**
 * bigw-api raises every verifier failure as `quest_verification_failed`, so
 * the reason only lives in the message text. Each rule rewrites one known
 * message; an unmatched message is shown as the backend wrote it, so a
 * reworded backend message degrades to plain text rather than wrong copy.
 * Order matters: the first match wins.
 */
const VERIFICATION_RULES: ReadonlyArray<[RegExp, (match: RegExpMatchArray) => string]> = [
  [
    /not a member of the Telegram group/i,
    () => "You haven't joined the Telegram group yet. Join it, then collect your points.",
  ],
  [
    /not a member of the Discord server/i,
    () => "You haven't joined the Discord server yet. Join it, then collect your points.",
  ],
  [
    /^link your (\w+) account first/i,
    (m) => `Link your ${platformLabel(m[1])} account from your profile first.`,
  ],
  [/please wait (\d+)s/i, (m) => `Please wait ${m[1]}s before trying again.`],
  // Upstream / misconfiguration failures carry internals the user can't act on.
  [/API error|could not verify|quest config is missing|malformed/i, () => VERIFY_LATER],
];

function verificationCopy(message: string): string {
  for (const [pattern, toCopy] of VERIFICATION_RULES) {
    const match = message.match(pattern);
    if (match) return toCopy(match);
  }
  return sentenceCase(message) || VERIFY_LATER;
}

function envelope(err: unknown): ApiErrorResponse['error'] | null {
  if (!(err instanceof AxiosError)) return null;
  const data = err.response?.data as Partial<ApiErrorResponse> | undefined;
  const error = data?.error;
  return error && typeof error === 'object' && typeof error.code === 'string' ? error : null;
}

/** The stable machine code from bigw-api's error envelope, if there is one. */
export function apiErrorCode(err: unknown): string | null {
  return envelope(err)?.code ?? null;
}

/**
 * A sentence fit to show the user for a failed bigw-api call, in place of
 * axios's "Request failed with status code N". Reads the
 * `{ error: { code, message } }` envelope every bigw-api error carries.
 */
export function readableApiError(err: AxiosError): string {
  // No response at all: offline, DNS, CORS or the request timed out.
  if (!err.response) return NETWORK_ERROR;

  const error = envelope(err);
  const status = err.response.status;
  if (!error) return status === 429 ? CODE_COPY.rate_limited : GENERIC_ERROR;

  const known = CODE_COPY[error.code];
  if (known) return known;

  const message = typeof error.message === 'string' ? error.message : '';
  if (error.code === 'quest_verification_failed') return verificationCopy(message);
  if (error.code === 'social_link_conflict') {
    return sentenceCase(
      message.replace(/^this (\w+) account/i, (_, p: string) => `this ${platformLabel(p)} account`),
    );
  }

  // Other 4xx codes already carry a message written for the caller; 5xx ones
  // describe our own failure, which the user can only retry.
  if (status >= 500 || !message) return GENERIC_ERROR;
  return sentenceCase(message);
}
